import { AdminOrder, AdminOrderStatus } from '../../../mocks/adminOrders';

interface OrdersTableProps {
  orders: AdminOrder[];
  onSelectOrder: (order: AdminOrder) => void;
  onStatusChange: (id: string, status: AdminOrderStatus) => void;
}

const statusConfig: Record<AdminOrderStatus, { label: string; className: string; icon: string }> = {
  payment_pending: { label: 'Awaiting Payment', className: 'bg-violet-50 text-violet-700 border-violet-200', icon: 'ri-bank-card-line' },
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-time-line' },
  processing: { label: 'Processing', className: 'bg-sky-50 text-sky-700 border-sky-200', icon: 'ri-loader-3-line' },
  on_way: { label: 'On the Way', className: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: 'ri-motorbike-line' },
  delivered: { label: 'Delivered', className: 'bg-green-50 text-green-700 border-green-200', icon: 'ri-checkbox-circle-line' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 border-red-200', icon: 'ri-close-circle-line' },
};

const nextStatus: Partial<Record<AdminOrderStatus, AdminOrderStatus>> = {
  pending: 'processing',
  processing: 'on_way',
  on_way: 'delivered',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function OrdersTable({ orders, onSelectOrder, onStatusChange }: OrdersTableProps) {
  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 py-16 flex flex-col items-center justify-center gap-3">
        <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center">
          <i className="ri-file-list-3-line text-stone-400 text-xl" />
        </div>
        <p className="text-stone-400 text-sm">No orders found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-stone-50 border-b border-stone-200">
            <th className="text-left px-5 py-3 text-xs font-semibold text-stone-400 uppercase tracking-wide">Order</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-stone-400 uppercase tracking-wide">Customer</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-stone-400 uppercase tracking-wide">Items</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-stone-400 uppercase tracking-wide">Total</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-stone-400 uppercase tracking-wide">Status</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-stone-400 uppercase tracking-wide">Date</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-stone-400 uppercase tracking-wide">Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, idx) => {
            const cfg = statusConfig[order.status];
            const next = nextStatus[order.status];
            return (
              <tr
                key={order.id}
                className={`border-b border-stone-100 hover:bg-stone-50/60 transition cursor-pointer ${
                  idx === orders.length - 1 ? 'border-none' : ''
                }`}
                onClick={() => onSelectOrder(order)}
              >
                {/* Order ID */}
                <td className="px-5 py-3.5">
                  <span className="font-semibold text-stone-800">{order.id}</span>
                </td>

                {/* Customer */}
                <td className="px-5 py-3.5">
                  <p className="font-medium text-stone-800">{order.customer}</p>
                  <p className="text-xs text-stone-400">{order.address.split(',')[0]}</p>
                </td>

                {/* Items summary */}
                <td className="px-5 py-3.5 max-w-[180px]">
                  <p className="text-stone-600 truncate">
                    {order.items.map((i) => `${i.name} ×${i.qty}`).join(', ')}
                  </p>
                </td>

                {/* Total */}
                <td className="px-5 py-3.5">
                  <span className="font-semibold text-stone-800">GYD ${order.total.toLocaleString()}</span>
                  {order.paymentMethod === 'Cash on Delivery' && order.changeAmount !== undefined && order.changeAmount >= 0 && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <div className="w-3.5 h-3.5 flex items-center justify-center text-amber-500">
                        <i className="ri-coins-line text-xs" />
                      </div>
                      <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">
                        Troco: GYD ${order.changeAmount.toLocaleString()}
                      </span>
                    </div>
                  )}
                </td>

                {/* Status badge */}
                <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg ? cfg.className : 'bg-stone-50 text-stone-500 border-stone-200'}`}>
                    <i className={`${cfg ? cfg.icon : 'ri-question-line'} text-xs`} />
                    {cfg ? cfg.label : order.status}
                  </span>
                </td>

                {/* Date */}
                <td className="px-5 py-3.5">
                  <span className="text-stone-500 text-xs">{formatDate(order.createdAt)}</span>
                </td>

                {/* Actions */}
                <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    {order.status === 'payment_pending' ? (
                      <span className="px-2.5 py-1.5 bg-stone-100 text-stone-400 text-xs font-medium rounded-lg whitespace-nowrap">
                        Awaiting approval
                      </span>
                    ) : next ? (
                      <button
                        onClick={() => onStatusChange(order.id, next)}
                        className="px-2.5 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-arrow-right-line mr-1" />
                        {statusConfig[next].label}
                      </button>
                    ) : null}
                    <button
                      onClick={() => onSelectOrder(order)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-stone-100 transition cursor-pointer"
                      title="View details"
                    >
                      <i className="ri-eye-line text-stone-400 text-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
